import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { getQueryTimeoutMs, MAX_RETRIES } from '#pplx/frontend/api-clients/core/utils.js';
import { getComputerSpeedOptions, getModelConfigIds, } from '#pplx/frontend/ask-models/computerSpeedOptions.js';
import { getSearchModelDisplayName, getSearchModelProvider, isSearchMode, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { modelsClient } from '#pplx_generated/typespec/rest/models/client.js';
export const COMPUTER_HARNESS_MODES = ['instant', 'light', 'standard', 'high', 'ultra'];
export const getAvailableComputerHarnessModes = (modes) => {
    const available = COMPUTER_HARNESS_MODES.filter((mode) => modes?.[mode] !== undefined);
    // Missing/older catalogs must not expose the default-off Instant rollout.
    return available.length > 0
        ? available
        : COMPUTER_HARNESS_MODES.filter((mode) => mode !== 'instant');
};
export const getEffectiveComputerHarnessMode = (mode, modes, defaultMode) => modes.find((available) => available === mode) ??
    modes.find((available) => available === defaultMode) ??
    modes[0] ??
    mode;
export const createGetSearchModelsQuery = ({ config, models, default_models, agentic_research_compare_models, computer_harness, }) => {
    const modelConfigs = config;
    const modelLookup = modelConfigs.reduce((map, config) => {
        for (const model of getModelConfigIds(config)) {
            map.set(model, config);
        }
        return map;
    }, new Map());
    const getModelConfig = (model) => {
        return modelLookup.get(model);
    };
    const getModelLabel = (model) => {
        return models[model]?.label ?? getSearchModelDisplayName(model);
    };
    const getModelShortName = (model) => {
        return models[model]?.short_name ?? undefined;
    };
    const getPillLabel = (model) => {
        return getModelShortName(model) ?? getModelLabel(model);
    };
    const getModelDescription = (model) => {
        return models[model]?.description;
    };
    const getModelSubheading = (model) => {
        const config = getModelConfig(model);
        return config?.subheading ?? undefined;
    };
    const getModelProvider = (model) => {
        return models[model]?.provider ?? getSearchModelProvider(model);
    };
    const getModelAllowedReasoningEfforts = (model) => {
        return models[model]?.computer_effort?.allowed_reasoning_efforts;
    };
    const getModelDefaultReasoningEffort = (model) => {
        return models[model]?.computer_effort?.default_reasoning_effort;
    };
    const getModelMode = (model) => {
        const mode = models[model]?.mode;
        return isSearchMode(mode) ? mode : undefined;
    };
    const getDefaultModelForMode = (mode) => {
        return default_models[mode] ?? default_models.search;
    };
    const getDefaultModelsForMode = (mode) => {
        if (mode === 'agentic_research') {
            return [...agentic_research_compare_models];
        }
        return [default_models[mode] ?? default_models.search];
    };
    const getConfigForSearchMode = (mode) => {
        return modelConfigs.filter((config) => getModelConfigIds(config).some((model) => getModelMode(model) === mode));
    };
    function isSearchModel(model) {
        return typeof model === 'string' && model in models;
    }
    const isReasoningModelVariant = (model) => {
        if (!model)
            return false;
        const modelConfig = getModelConfig(model);
        return modelConfig?.reasoning_model === model;
    };
    const supportsModelSwitching = (model) => getModelConfig(model)?.support_model_switching ?? false;
    return {
        config_schema: 'v1',
        models,
        config: modelConfigs,
        default_models,
        agentic_research_compare_models,
        computer_harness,
        getModelConfig,
        getModelLabel,
        getModelShortName,
        getPillLabel,
        getModelDescription,
        getModelSubheading,
        getModelProvider,
        getModelAllowedReasoningEfforts,
        getModelDefaultReasoningEffort,
        getModelMode,
        getDefaultModelForMode,
        getDefaultModelsForMode,
        getConfigForSearchMode,
        isSearchModel,
        isReasoningModelVariant,
        supportsModelSwitching,
    };
};
/**
 * Normalize the v2 response (split search/computer lists) into the v1-shaped
 * config the query helpers already consume. Each computer row collapses its
 * single `model` id into `non_reasoning_model` and carries v2-only fields such
 * as `tag_text` and `support_model_switching`, so every existing callback keeps
 * working unchanged.
 */
export const adaptModelsConfigV2ToV1 = (data) => {
    const computerConfigs = data.computer_config.map((config) => ({
        label: config.label,
        description: config.description,
        subheading: config.subheading,
        has_new_tag: config.has_new_tag,
        tag_text: config.tag_text,
        subscription_tier: config.subscription_tier,
        non_reasoning_model: config.model,
        reasoning_model: null,
        speed_options: getComputerSpeedOptions(config),
        text_only_model: false,
        audience: config.audience,
        is_default: config.is_default,
        support_model_switching: config.support_model_switching,
    }));
    return {
        config_schema: 'v1',
        models: data.models,
        config: [...data.search_config, ...computerConfigs],
        default_models: data.default_models,
        agentic_research_compare_models: data.agentic_research_compare_models,
        computer_harness: data.computer_harness,
    };
};
export const getSearchModelsQueryKey = () => QueryClientHelper.makeQueryKey('/rest/models/config/v2');
export const getSearchModelConfigQuery = async ({ reason, }) => {
    const { error, data, response } = await modelsClient.GET('/rest/models/config/v2', reason, {
        timeoutMs: getQueryTimeoutMs(),
        numRetries: MAX_RETRIES,
    });
    if (error) {
        throw new ApiClientsError('API_CLIENTS_ERROR', {
            message: `Failed to fetch models (v2)`,
            cause: error,
            status: response.status ?? 0,
        });
    }
    const v1Shaped = adaptModelsConfigV2ToV1(data);
    return {
        ...createGetSearchModelsQuery(v1Shaped),
        organizationModelPolicy: data.organization_model_policy,
    };
};
//# sourceMappingURL=getSearchModelConfigQuery.js.map